import React, { useState } from "react";
import { MessageCircle, Phone, Clock } from "lucide-react";
import InfoPage from "./Infopage";
import { Helmet } from "react-helmet-async";

// WhatsApp number in international format: digits only, no "+", spaces or dashes.
const WHATSAPP_NUMBER = "254112866754";

const TOPICS = [
  "Order help",
  "Returns and refunds",
  "Product question",
  "Something else",
];
const EMPTY = { name: "", topic: TOPICS[0], message: "" };

const buildWhatsAppUrl = ({ name, topic, message }) => {
  const text = `Hello, my name is ${name.trim()}.\nTopic: ${topic}\n\n${message.trim()}`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

function ContactUs() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [waUrl, setWaUrl] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Enter your name.";
    if (form.message.trim().length < 10)
      e.message = "Write at least 10 characters.";
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    const url = buildWhatsAppUrl(form);
    window.open(url, "_blank", "noopener,noreferrer");
    setWaUrl(url);
  };

  return (
    <InfoPage
      title="Contact us"
      intro="Send us a message on WhatsApp and we'll reply within minutes."
    >
      <Helmet>
        <title>Contact Ninety One | Clothing Store in Nairobi, Kenya</title>
        <meta
          name="description"
          content="Contact Ninety One on WhatsApp for order help, returns, or product questions. Streetwear and fashion store in Nairobi, Kenya. Open Monday to Saturday, 8:00 to 18:00."
        />
        <link rel="canonical" href="https://ninetyone.co.ke/contact-us" />
      </Helmet>
      <div className="info-contact">
        <div className="info-contact-info">
          <div className="info-item">
            <span className="info-icon">
              <MessageCircle aria-hidden="true" />
            </span>
            <h3 className="info-h3">WhatsApp</h3>
            <p>
              <a
                className="info-link"
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                +254 112 866 754
              </a>
            </p>
          </div>
          <div className="info-item">
            <span className="info-icon">
              <Phone aria-hidden="true" />
            </span>
            <h3 className="info-h3">Phone</h3>
            <p>
              <a className="info-link" href={`tel:+${WHATSAPP_NUMBER}`}>
                +254 112 866 754
              </a>
            </p>
          </div>
          <div className="info-item">
            <span className="info-icon">
              <Clock aria-hidden="true" />
            </span>
            <h3 className="info-h3">Hours</h3>
            <p>Monday to Saturday, 8:00 to 18:00</p>
          </div>
        </div>

        {waUrl ? (
          <div className="info-success" role="status">
            <h2 className="info-h2">WhatsApp opened</h2>
            <p>
              Thanks, {form.name}. Your message is ready in WhatsApp. Tap send
              to deliver it to us.
            </p>
            <p>
              Nothing opened?{" "}
              <a
                className="info-link"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open WhatsApp
              </a>
            </p>
            <button
              className="info-btn info-btn--secondary"
              onClick={() => {
                setForm(EMPTY);
                setWaUrl(null);
              }}
            >
              Write another message
            </button>
          </div>
        ) : (
          <form className="info-form" onSubmit={submit} noValidate>
            <Field label="Name" id="c-name" error={errors.name}>
              <input
                id="c-name"
                value={form.name}
                onChange={set("name")}
                autoComplete="name"
              />
            </Field>
            <Field label="Topic" id="c-topic">
              <select id="c-topic" value={form.topic} onChange={set("topic")}>
                {TOPICS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Message" id="c-message" error={errors.message}>
              <textarea
                id="c-message"
                rows={5}
                value={form.message}
                onChange={set("message")}
              />
            </Field>
            <button type="submit" className="info-btn info-btn--primary">
              Send on WhatsApp
            </button>
          </form>
        )}
      </div>
    </InfoPage>
  );
}

function Field({ label, id, error, children }) {
  return (
    <div className={`info-field ${error ? "info-field--error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error && <span className="info-error">{error}</span>}
    </div>
  );
}

export default ContactUs;
